import {
    BookOpen,
    Heart,
    Sparkles,
    MessagesSquare,
    LineChart,
} from "lucide-react";
import { BOOKS_WALL_IMG, COZY_LIGHTS_IMG } from "./assets";

const FeatureCard = ({ icon, title, body, accent, testId }) => (
    <div
        data-testid={testId}
        className="relative rounded-[2rem] bg-white border border-[#E5D7C5] p-8 shadow-soft hover:shadow-lift hover:-translate-y-0.5 transition-all duration-300"
    >
        <span
            className="inline-flex items-center justify-center w-11 h-11 rounded-full text-white"
            style={{ backgroundColor: accent }}
        >
            {icon}
        </span>
        <h3 className="font-serif text-2xl mt-5 leading-tight text-[#2D2D2D]">
            {title}
        </h3>
        <p className="mt-3 text-[15px] text-[#6B655C] leading-relaxed">
            {body}
        </p>
    </div>
);

const BookClubsCard = () => (
    <div
        data-testid="feature-book-clubs"
        className="md:col-span-2 md:row-span-2 relative overflow-hidden rounded-[2rem] bg-white border border-[#E5D7C5] shadow-soft group hover:shadow-lift hover:-translate-y-0.5 transition-all duration-500 flex flex-col"
    >
        <div className="p-8 md:p-12 max-w-md">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C4956A]/15 text-[#C4956A] text-xs uppercase tracking-widest font-semibold">
                <BookOpen size={14} /> Book clubs
            </span>
            <h3 className="font-serif text-3xl md:text-4xl mt-5 leading-tight">
                30+ cozy book clubs, one chapter a week.
            </h3>
            <p className="mt-4 text-[#6B655C] text-[15px] leading-relaxed">
                Romantasy to self-help — read along at your own pace with
                weekly prompts that actually make you feel something.
            </p>
        </div>
        <div className="mt-auto overflow-hidden">
            <img
                src={BOOKS_WALL_IMG}
                alt="Wall of open book pages"
                className="w-full h-56 md:h-64 object-cover group-hover:scale-[1.04] transition-transform duration-700"
            />
        </div>
    </div>
);

const TrackerCard = () => (
    <div
        data-testid="feature-tracker"
        className="md:col-span-2 relative overflow-hidden rounded-[2rem] bg-[#2D2D2D] text-[#FDF6EE] p-8 md:p-12 group hover:shadow-lift transition-all duration-500"
    >
        <div className="max-w-md relative z-10">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C4956A]/30 text-[#FDF6EE] text-xs uppercase tracking-widest font-semibold">
                <LineChart size={14} /> Reading tracker
            </span>
            <h3 className="font-serif text-3xl md:text-4xl mt-5 leading-tight text-white">
                Your reading life, beautifully kept.
            </h3>
            <p className="mt-4 text-[#FDF6EE]/75 text-[15px] leading-relaxed">
                Every page, every review, every yearly goal — a keepsake
                of every book that ever kept you up too late.
            </p>
        </div>
        <img
            src={COZY_LIGHTS_IMG}
            alt=""
            className="absolute -right-10 -bottom-10 w-72 h-72 object-cover rounded-full opacity-40 blur-[1px] group-hover:opacity-60 transition-opacity"
        />
    </div>
);

export const Features = () => (
    <section
        id="features"
        data-testid="features-section"
        className="py-24 md:py-32 relative"
    >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
            <div className="max-w-2xl mb-16">
                <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C4956A]">
                    What's inside
                </span>
                <h2 className="font-serif text-4xl md:text-5xl leading-tight mt-3 text-[#2D2D2D]">
                    Everything a book lover needs,{" "}
                    <span className="italic">
                        in one warm corner of the internet
                    </span>
                    .
                </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-[minmax(220px,auto)] gap-6">
                <BookClubsCard />
                <FeatureCard
                    testId="feature-swipe"
                    icon={<Heart size={18} />}
                    title="Swipe to discover"
                    body="Say yes to the covers that give you butterflies. Skip the rest, guilt-free."
                    accent="#E86A58"
                />
                <FeatureCard
                    testId="feature-social"
                    icon={<Sparkles size={18} />}
                    title="Social media manager"
                    body="Your BookTok, on autopilot — ready-to-post content in seconds, burnout not included."
                    accent="#C4956A"
                />
                <FeatureCard
                    testId="feature-chat"
                    icon={<MessagesSquare size={18} />}
                    title="Chat rooms"
                    body="Finally — people who understand why you're still up at 2am on chapter 23."
                    accent="#C4956A"
                />
                <TrackerCard />
            </div>
        </div>
    </section>
);
